import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from "@angular/core";
import { LazyLoadQueueService } from "../../services/lazy-load-queue.service";

/**
 * Lazy-load directive for images.
 * Only sets img src when the element enters the viewport (Intersection Observer).
 * Uses queue to limit concurrent loads - avoids 100+ images competing for connections.
 */
@Directive({
  selector: "img[appLazyLoad]",
})
export class LazyLoadDirective implements OnInit, OnChanges, OnDestroy {
  @Input() appLazyLoad: string;

  private observer: IntersectionObserver | null = null;
  private hasLoaded = false;
  private fallbackTimer: any = null;
  private unlistenLoad: (() => void) | null = null;
  private unlistenError: (() => void) | null = null;
  private parentElement: HTMLElement | null = null;
  private destroyed = false;

  /** 1x1 transparent GIF - prevents broken image icon before load */
  private static readonly PLACEHOLDER =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

  constructor(
    private el: ElementRef<HTMLImageElement>,
    private renderer: Renderer2,
    private queue: LazyLoadQueueService
  ) {}

  private readonly LOADING_CLASS = "lazy-loading";
  /** Class on parent - shows loading spinner overlay */
  private readonly PENDING_CLASS = "lazy-load-pending";

  ngOnInit(): void {
    if (!this.appLazyLoad || this.hasLoaded) {
      return;
    }

    this.renderer.addClass(this.el.nativeElement, this.LOADING_CLASS);
    this.parentElement = this.el.nativeElement.parentElement;
    if (this.parentElement) {
      this.renderer.addClass(this.parentElement, this.PENDING_CLASS);
    }
    this.renderer.setAttribute(
      this.el.nativeElement,
      "src",
      LazyLoadDirective.PLACEHOLDER
    );

    // Check if IntersectionObserver is supported
    if (typeof IntersectionObserver === "undefined") {
      this.loadImage();
      return;
    }

    const onIntersect = (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && !this.hasLoaded) {
        this.loadImage();
        this.disconnect();
      }
    };

    // rootMargin 150px - balance preload vs lag
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(onIntersect);
      },
      {
        root: null,
        rootMargin: "150px 0px 150px 0px",
        threshold: 0.01,
      }
    );

    this.observer.observe(this.el.nativeElement);

    // Fallback: IntersectionObserver may not fire immediately for elements
    // already in viewport. Check after layout and load if visible.
    requestAnimationFrame(() => {
      if (this.hasLoaded || !this.observer) return;
      if (this.isInViewport()) {
        this.loadImage();
        this.disconnect();
        return;
      }
      // Safety: force load after 300ms if still not loaded (handles layout delays)
      this.fallbackTimer = setTimeout(() => {
        if (!this.hasLoaded && this.appLazyLoad) {
          this.loadImage();
          this.disconnect();
        }
        this.fallbackTimer = null;
      }, 300);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.appLazyLoad && changes.appLazyLoad.firstChange === false) {
      this.hasLoaded = false;
      if (this.unlistenLoad) {
        this.unlistenLoad();
        this.unlistenLoad = null;
      }
      if (this.unlistenError) {
        this.unlistenError();
        this.unlistenError = null;
      }
      this.disconnect();
      this.ngOnInit();
    }
  }

  private isInViewport(): boolean {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const margin = 150;
    return (
      rect.top < window.innerHeight + margin &&
      rect.bottom > -margin &&
      rect.left < window.innerWidth + margin &&
      rect.right > -margin
    );
  }

  private loadImage(): void {
    if (!this.appLazyLoad || this.hasLoaded) {
      return;
    }
    this.hasLoaded = true;

    this.queue.acquire().then(() => {
      if (this.destroyed || !this.appLazyLoad) {
        this.queue.release();
        return;
      }
      const img = this.el.nativeElement;
      const onLoad = () => this.onImageLoad();
      this.unlistenLoad = this.renderer.listen(img, "load", onLoad);
      this.unlistenError = this.renderer.listen(img, "error", onLoad);
      this.renderer.setAttribute(img, "decoding", "async");
      this.renderer.setAttribute(img, "src", this.appLazyLoad);
    });
  }

  private onImageLoad(): void {
    this.queue.release();
    this.renderer.removeClass(this.el.nativeElement, this.LOADING_CLASS);
    if (this.parentElement) {
      this.renderer.removeClass(this.parentElement, this.PENDING_CLASS);
      this.parentElement = null;
    }
    if (this.unlistenLoad) {
      this.unlistenLoad();
      this.unlistenLoad = null;
    }
    if (this.unlistenError) {
      this.unlistenError();
      this.unlistenError = null;
    }
  }

  /** Only cleanup observer/timer - keep load/error listeners until image loads */
  private disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    // Do NOT remove load/error listeners here - onImageLoad needs them to fire
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.parentElement) {
      this.renderer.removeClass(this.parentElement, this.PENDING_CLASS);
    }
    const wasLoading = !!this.unlistenLoad;
    if (this.unlistenLoad) {
      this.unlistenLoad();
      this.unlistenLoad = null;
    }
    if (this.unlistenError) {
      this.unlistenError();
      this.unlistenError = null;
    }
    if (wasLoading) this.queue.release();
    this.disconnect();
  }
}
